import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { appPath } from "@/lib/paths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Milk, Loader2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";
import { forgotPassword } from "@/lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { signIn } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  const submitForgot = async () => {
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    try {
      await forgotPassword(forgotEmail.trim());
      toast.success(t("login.forgotSent"));
      setForgotOpen(false);
      setForgotEmail("");
    } catch {
      toast.success(t("login.forgotSent")); // never reveal whether the email exists
      setForgotOpen(false);
      setForgotEmail("");
    } finally {
      setForgotSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await signIn(email.trim(), password);
      navigate(u?.role === "superadmin" ? "/superadmin" : (u?.role === "client" ? "/portal" : appPath()));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, t("login.failed")) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center header-band px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--brand))] text-white shadow-lg">
            <Milk className="h-7 w-7" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">{t("login.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("login.subtitle")}</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dairyops.com" data-testid="login-email-input" required />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("login.password")}</Label>
                  <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
                    <DialogTrigger asChild>
                      <button type="button" className="text-xs font-medium text-[hsl(var(--brand))] hover:underline" data-testid="forgot-password-link">
                        {t("login.forgotPassword")}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>{t("login.forgotTitle")}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t("login.forgotHint")}</p>
                        <div className="space-y-1.5">
                          <Label htmlFor="forgot-email">{t("login.email")}</Label>
                          <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitForgot()} placeholder="you@dairyops.com" data-testid="forgot-email-input" />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setForgotOpen(false)}>{t("common.cancel")}</Button>
                        <Button onClick={submitForgot} disabled={forgotSending} data-testid="forgot-send-button">
                          {forgotSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          {t("login.forgotSend")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" required />
              </div>
              {error && (
                <div className="rounded-lg badge-bad px-3 py-2 text-sm" data-testid="login-error">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("login.signIn")}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t("login.noAccount")}{" "}
              <a href="/signup" className="font-semibold text-[hsl(var(--brand))] hover:underline" data-testid="login-signup-link">
                {t("login.signUpCta")}
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
